import type {
  StrategyRdAppliedLevels,
  StrategyRdDirection,
  StrategyRdExitLeg,
  StrategyRdMatch,
  StrategyRdOhlcBar,
  StrategyRdRuleOffset,
  StrategyRdRuleTemplate,
  StrategyRdStopDistanceBound,
  StrategyRdStopStep,
} from "../types/strategyRd";
import type { ExecutionTradeOverlay } from "../types/trade";
import {
  resolveStrategyRdPipSize,
  resolveStrategyRdTickSize,
  strategyRdIsStructuralStopAnchor,
  strategyRdIsStructuralTargetMode,
} from "./strategyRd";
import {
  strategyRdMatchDisplayDirection,
  strategyRdMatchEntryBarTime,
  strategyRdPathAppliedLevels,
} from "./strategyRdPath";

function asFinite(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeBarTime(raw: string): string {
  const s = raw.trim();
  if (s.endsWith("+00:00")) return `${s.slice(0, -6)}Z`;
  return s;
}

export function strategyRdIndexOfBarTime(
  candles: readonly StrategyRdOhlcBar[],
  barTime: string
): number {
  const want = normalizeBarTime(barTime);
  if (!want) return -1;
  for (let i = 0; i < candles.length; i += 1) {
    const got = normalizeBarTime(candles[i]?.timestamp ?? "");
    if (got === want || got.replace(/Z$/, "") === want.replace(/Z$/, "")) return i;
  }
  return -1;
}

/**
 * Chart times for a trade closed in parts: each leg's `bars` count candles after the entry
 * candle; the last leg is the trade's own exit. A leg that can't be placed is left out.
 */
export function strategyRdExitLegMarkers(opts: {
  candles: readonly StrategyRdOhlcBar[];
  entryBarTime: string | null | undefined;
  exitTime: string | null | undefined;
  legs: readonly StrategyRdExitLeg[];
}): Array<{ time: string; price: number; kind: string; frac: number }> {
  const { candles, legs } = opts;
  if (legs.length < 2) return [];
  const start = opts.entryBarTime ? strategyRdIndexOfBarTime(candles, opts.entryBarTime) : -1;
  const exitMs = Date.parse(opts.exitTime || "");
  const out: Array<{ time: string; price: number; kind: string; frac: number }> = [];
  legs.forEach((leg, i) => {
    let time: string | null = null;
    if (i === legs.length - 1 && opts.exitTime) time = opts.exitTime;
    else if (start >= 0 && leg.bars != null) {
      time = candles[Math.min(candles.length - 1, start + Math.max(0, Math.round(leg.bars)))]?.timestamp ?? null;
      // Never after the trade's own exit.
      if (time && Number.isFinite(exitMs) && Date.parse(time) > exitMs) time = opts.exitTime ?? time;
    }
    if (time) out.push({ time, price: leg.price, kind: leg.kind, frac: leg.frac });
  });
  return out;
}

/**
 * The stop's moves for the chart (break-even, trail): the initial stop from the entry, then each
 * move from the candle it took effect on, never past the exit. Empty when the stop never moved.
 */
export function strategyRdStopPathSteps(opts: {
  candles: readonly StrategyRdOhlcBar[];
  entryBarTime: string | null | undefined;
  exitTime: string | null | undefined;
  path: readonly StrategyRdStopStep[] | null | undefined;
}): Array<{ time: string; price: number; why: string }> {
  const path = opts.path ?? [];
  if (path.length < 2) return [];
  const start = opts.entryBarTime ? strategyRdIndexOfBarTime(opts.candles, opts.entryBarTime) : -1;
  const exitMs = Date.parse(opts.exitTime || "");
  const out: Array<{ time: string; price: number; why: string }> = [];
  path.forEach((step, i) => {
    let time: string | null = null;
    if (i === 0) time = opts.entryBarTime ?? step.time ?? null;
    else if (step.time) time = step.time;
    else if (start >= 0) time = opts.candles[Math.min(opts.candles.length - 1, start + Math.max(0, Math.round(step.bars)))]?.timestamp ?? null;
    if (!time) return;
    // A move that would only apply after the exit candle never mattered.
    if (i > 0 && Number.isFinite(exitMs) && Date.parse(time) > exitMs) return;
    out.push({ time, price: step.price, why: step.why });
  });
  return out.length > 1 ? out : [];
}

const STRATEGY_RD_EXIT_REASON_LABELS: Record<string, string> = {
  time_stop: "Time stop",
  time_exit: "Clock exit",
  oscillator_exit: "StochRSI exit",
  ma_close_exit: "MA close exit",
};

/** Why a backtest trade closed, for its exit on the chart; null for a plain stop / target. */
export function strategyRdExitReason(opts: {
  result: string | null | undefined;
  note: string | null | undefined;
  stopPath: ReadonlyArray<{ why: string }>;
}): string | null {
  const notes = (opts.note || "").split("|");
  if (opts.result === "exit") {
    const key = notes.find((n) => STRATEGY_RD_EXIT_REASON_LABELS[n]);
    return key ? STRATEGY_RD_EXIT_REASON_LABELS[key] : "Exit";
  }
  if (opts.result === "stop" && opts.stopPath.length > 1) {
    const why = opts.stopPath[opts.stopPath.length - 1]?.why;
    if (why === "trail") return "Trail stop";
    if (why === "break_even") return "BE stop";
  }
  return null;
}

export function strategyRdComputeAtr(
  candles: readonly StrategyRdOhlcBar[],
  endIndex: number,
  length: number
): number | null {
  if (endIndex < 1 || length < 1) return null;
  const start = Math.max(1, endIndex - length + 1);
  const trs: number[] = [];
  for (let i = start; i <= endIndex; i += 1) {
    const h = asFinite(candles[i]?.high);
    const l = asFinite(candles[i]?.low);
    const pc = asFinite(candles[i - 1]?.close);
    if (h == null || l == null || pc == null) continue;
    trs.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  if (!trs.length) return null;
  return trs.reduce((a, b) => a + b, 0) / trs.length;
}

function offsetDistance(opts: {
  offset: StrategyRdRuleOffset;
  entry: number;
  atr: number | null;
  pipSize?: number | null;
  tickSize?: number | null;
}): number {
  const { offset, entry, atr, pipSize, tickSize } = opts;
  const kind = offset.kind;
  const val = Number(offset.value) || 0;
  if (kind === "none") return 0;
  if (kind === "beyond_bar" || kind === "beyond_level") {
    if (val > 0) {
      if (atr == null || atr <= 0) throw new Error(`${kind} with value requires ATR`);
      return val * atr;
    }
    return 0;
  }
  if (kind === "pct_price") return Math.abs(entry) * (val / 100);
  if (kind === "price") return val;
  if (kind === "atr_mult") {
    if (atr == null || atr <= 0) throw new Error("atr_mult requires ATR");
    return val * atr;
  }
  if (kind === "pips") {
    if (!(pipSize != null && pipSize > 0)) throw new Error("pips requires pip_size");
    return val * pipSize;
  }
  if (kind === "ticks") {
    const unit = tickSize != null && tickSize > 0 ? tickSize : pipSize;
    if (!(unit != null && unit > 0)) throw new Error("ticks requires tick_size");
    return val * unit;
  }
  return 0;
}

function activeStopBound(
  bound: StrategyRdStopDistanceBound | null | undefined
): StrategyRdStopDistanceBound | null {
  if (!bound) return null;
  const value = Number(bound.value);
  const kind = (bound.kind || "").trim().toLowerCase();
  if (!(value > 0)) return null;
  if (kind !== "pct_price" && kind !== "atr_mult" && kind !== "pips" && kind !== "ticks") {
    return null;
  }
  return { kind, value };
}

/**
 * Push the entry-to-stop distance out to the minimum and in to the maximum.
 * No bound leaves that side as it is. Minimum larger than maximum rejects the level.
 */
function boundStopDistance(opts: {
  risk: number;
  template: StrategyRdRuleTemplate;
  entry: number;
  atr: number | null;
  pipSize?: number | null;
  tickSize?: number | null;
}): number {
  const loBound = activeStopBound(opts.template.stop.min_distance);
  const hiBound = activeStopBound(opts.template.stop.max_distance);
  if (!loBound && !hiBound) return opts.risk;
  const span = (bound: StrategyRdStopDistanceBound) =>
    offsetDistance({
      offset: { kind: bound.kind, value: bound.value },
      entry: opts.entry,
      atr: opts.atr,
      pipSize: opts.pipSize,
      tickSize: opts.tickSize,
    });
  const lo = loBound ? span(loBound) : null;
  const hi = hiBound ? span(hiBound) : null;
  if (lo != null && hi != null && lo > hi) throw new Error("stop_min_above_max");
  let out = opts.risk;
  if (lo != null && out < lo) out = lo;
  if (hi != null && out > hi) out = hi;
  return out;
}

function invalidLevels(
  partial: Partial<StrategyRdAppliedLevels> & {
    direction: "long" | "short";
    invalid_reason: string;
  }
): StrategyRdAppliedLevels {
  return {
    entry: partial.entry ?? 0,
    stop: partial.stop ?? 0,
    target: partial.target ?? partial.entry ?? 0,
    direction: partial.direction,
    risk: partial.risk ?? 0,
    atr: partial.atr ?? null,
    valid: false,
    invalid_reason: partial.invalid_reason,
  };
}

/**
 * Mirror BE `apply_rules_to_bar` for live chart preview while editing the template.
 * Apply-to-all still goes through the API for outcomes.
 */
export function applyStrategyRdRulesToBar(opts: {
  candles: readonly StrategyRdOhlcBar[];
  barIndex: number;
  direction: StrategyRdDirection;
  template: StrategyRdRuleTemplate;
  symbol?: string | null;
  assetClass?: string | null;
}): StrategyRdAppliedLevels {
  const { candles, barIndex, template } = opts;
  const direction: "long" | "short" = opts.direction === "short" ? "short" : "long";
  const unitOpts = { symbol: opts.symbol, assetClass: opts.assetClass };
  const pipSize = resolveStrategyRdPipSize(template, unitOpts);
  const tickSize = resolveStrategyRdTickSize(template, unitOpts);

  // Custom rules read confluence prices only the BE knows — use the path's levels.
  if (template.stop.rule || template.target.rule || template.entry.mode === "rule") {
    return invalidLevels({ direction, invalid_reason: "rule_requires_path" });
  }
  if (barIndex < 0 || barIndex >= candles.length) {
    return invalidLevels({ direction, invalid_reason: "bar_index_out_of_range" });
  }
  const bar = candles[barIndex]!;
  const o = asFinite(bar.open);
  const h = asFinite(bar.high);
  const l = asFinite(bar.low);
  const c = asFinite(bar.close);
  if (o == null || h == null || l == null || c == null) {
    return invalidLevels({ direction, invalid_reason: "incomplete_ohlc" });
  }

  const atr = strategyRdComputeAtr(candles, barIndex, template.atr_length);
  let entry = c;
  if (template.entry.mode === "signal_open") {
    entry = o;
  } else if (template.entry.mode === "next_open") {
    if (barIndex + 1 >= candles.length) {
      return invalidLevels({
        entry: c,
        atr,
        direction,
        invalid_reason: "next_bar_missing",
      });
    }
    const nxt = asFinite(candles[barIndex + 1]?.open);
    if (nxt == null) {
      return invalidLevels({
        entry: c,
        atr,
        direction,
        invalid_reason: "next_bar_incomplete",
      });
    }
    entry = nxt;
  }

  let dist: number;
  try {
    dist = offsetDistance({
      offset: template.stop.offset,
      entry,
      atr,
      pipSize,
      tickSize,
    });
  } catch (err) {
    return invalidLevels({
      entry,
      atr,
      direction,
      invalid_reason: err instanceof Error ? err.message : "offset_error",
    });
  }

  const isLong = direction === "long";
  let anchor: number;
  if (strategyRdIsStructuralStopAnchor(template.stop.anchor)) {
    return invalidLevels({
      entry,
      atr,
      direction,
      invalid_reason: "structural_stop_requires_path",
    });
  }
  if (template.stop.anchor === "signal_close") {
    anchor = c;
  } else if (template.stop.anchor === "level_extreme") {
    // Preview without match meta: fall back to signal extreme.
    anchor = isLong ? l : h;
  } else {
    anchor = isLong ? l : h;
  }
  const stopRaw = isLong ? anchor - dist : anchor + dist;
  let risk = Math.abs(entry - stopRaw);
  if (risk <= 0) {
    return invalidLevels({
      entry,
      stop: stopRaw,
      atr,
      direction,
      invalid_reason: "zero_risk",
    });
  }
  if (isLong && stopRaw >= entry) {
    return invalidLevels({
      entry,
      stop: stopRaw,
      risk,
      atr,
      direction,
      invalid_reason: "stop_not_below_entry",
    });
  }
  if (!isLong && stopRaw <= entry) {
    return invalidLevels({
      entry,
      stop: stopRaw,
      risk,
      atr,
      direction,
      invalid_reason: "stop_not_above_entry",
    });
  }

  let stop = stopRaw;
  try {
    const bounded = boundStopDistance({
      risk,
      template,
      entry,
      atr,
      pipSize,
      tickSize,
    });
    if (bounded !== risk) {
      risk = bounded;
      stop = isLong ? entry - risk : entry + risk;
    }
  } catch (err) {
    return invalidLevels({
      entry,
      stop: stopRaw,
      risk,
      atr,
      direction,
      invalid_reason: err instanceof Error ? err.message : "stop_min_above_max",
    });
  }

  const tmode = template.target.mode;
  const tval = Number(template.target.value);
  if (strategyRdIsStructuralTargetMode(tmode)) {
    return invalidLevels({
      entry,
      stop,
      risk,
      atr,
      direction,
      invalid_reason: "structural_target_requires_path",
    });
  }
  let move: number;
  try {
    if (tmode === "r_multiple") move = risk * tval;
    else if (tmode === "atr_mult") {
      if (atr == null || atr <= 0) throw new Error("atr_mult target requires ATR");
      move = atr * tval;
    } else if (tmode === "pct_price") move = Math.abs(entry) * (tval / 100);
    else if (tmode === "pips") move = tval * pipSize;
    else if (tmode === "ticks") move = tval * tickSize;
    else throw new Error(`unknown target mode ${tmode}`);
  } catch (err) {
    return invalidLevels({
      entry,
      stop,
      risk,
      atr,
      direction,
      invalid_reason: err instanceof Error ? err.message : "target_error",
    });
  }

  return {
    entry,
    stop,
    target: isLong ? entry + move : entry - move,
    direction,
    risk,
    atr,
    valid: true,
    invalid_reason: null,
  };
}

/**
 * Scan boxes share the preview fill.
 * Min/max stop, when set, move that fill-to-stop distance, and an R target follows it.
 */
function anchorOverlayToPathFill(opts: {
  entry: number;
  stop: number;
  target: number;
  path: StrategyRdMatch["path"];
  direction: "long" | "short";
  template: StrategyRdRuleTemplate;
  atr: number | null;
  pipSize?: number | null;
  tickSize?: number | null;
}): { entry: number; stop: number; target: number } | null {
  const fill =
    opts.path?.status === "ready" ? asFinite(opts.path.entry_price) : null;
  if (fill == null) {
    return { entry: opts.entry, stop: opts.stop, target: opts.target };
  }
  const isLong = opts.direction === "long";
  let stop = opts.stop;
  let target = opts.target;
  const stopOk = isLong ? stop < fill : stop > fill;
  if (!stopOk) {
    const pathStop = asFinite(opts.path?.stop_price);
    if (pathStop != null && (isLong ? pathStop < fill : pathStop > fill)) {
      stop = pathStop;
      const pathTarget = asFinite(opts.path?.target_price);
      if (pathTarget != null) target = pathTarget;
    } else {
      return { entry: opts.entry, stop: opts.stop, target: opts.target };
    }
  }
  let risk = Math.abs(fill - stop);
  try {
    const bounded = boundStopDistance({
      risk,
      template: opts.template,
      entry: fill,
      atr: opts.atr,
      pipSize: opts.pipSize,
      tickSize: opts.tickSize,
    });
    if (bounded !== risk) {
      risk = bounded;
      stop = isLong ? fill - risk : fill + risk;
    }
  } catch {
    return null;
  }
  const mode = (opts.template.target.mode || "").trim().toLowerCase();
  const rVal = Number(opts.template.target.value);
  if (mode === "r_multiple" && Number.isFinite(rVal) && rVal > 0) {
    target = isLong ? fill + risk * rVal : fill - risk * rVal;
  } else if (!strategyRdIsStructuralTargetMode(mode)) {
    const reward = Math.abs(opts.target - opts.entry);
    target = isLong ? fill + reward : fill - reward;
  }
  return { entry: fill, stop, target };
}

/** Trade Review–style overlay row for one Lab match (live rules preview). */
export function strategyRdMatchExecutionOverlay(opts: {
  tradeId: string;
  match: StrategyRdMatch;
  candles: readonly StrategyRdOhlcBar[];
  template: StrategyRdRuleTemplate;
  symbol?: string | null;
  assetClass?: string | null;
}): ExecutionTradeOverlay | null {
  const direction = strategyRdMatchDisplayDirection(opts.match);
  if (direction !== "long" && direction !== "short") return null;

  // Structural stop/target are path-owned (BE). Prefer ready path prices so the
  // chart always keeps red/green position boxes — never invent H/L on FE.
  const pathLevels = strategyRdPathAppliedLevels(opts.match.path);
  const structural =
    strategyRdIsStructuralStopAnchor(opts.template.stop.anchor) ||
    strategyRdIsStructuralTargetMode(opts.template.target.mode);

  let entry: number | null = null;
  let stop: number | null = null;
  let target: number | null = null;

  if (structural && pathLevels) {
    entry = pathLevels.entry;
    stop = pathLevels.stop;
    target = pathLevels.target;
  } else {
    const levels = previewStrategyRdLevels({
      candles: opts.candles,
      barTime: opts.match.bar_time,
      direction,
      template: opts.template,
      symbol: opts.symbol,
      assetClass: opts.assetClass,
    });
    if (levels?.valid) {
      const anchored = anchorOverlayToPathFill({
        entry: levels.entry,
        stop: levels.stop,
        target: levels.target,
        path: opts.match.path,
        direction,
        template: opts.template,
        atr: levels.atr ?? null,
        pipSize: resolveStrategyRdPipSize(opts.template, {
          symbol: opts.symbol,
          assetClass: opts.assetClass,
        }),
        tickSize: resolveStrategyRdTickSize(opts.template, {
          symbol: opts.symbol,
          assetClass: opts.assetClass,
        }),
      });
      if (!anchored) return null;
      entry = anchored.entry;
      stop = anchored.stop;
      target = anchored.target;
    } else if (pathLevels) {
      entry = pathLevels.entry;
      stop = pathLevels.stop;
      target = pathLevels.target;
    }
  }

  if (
    entry == null ||
    stop == null ||
    target == null ||
    !Number.isFinite(entry) ||
    !Number.isFinite(stop) ||
    !Number.isFinite(target)
  ) {
    return null;
  }

  return {
    trade_id: opts.tradeId,
    symbol: opts.match.symbol,
    direction,
    entry_time: strategyRdMatchEntryBarTime(opts.match),
    exit_time: null,
    entry_price: entry,
    exit_price: null,
    sl_price: stop,
    tp_price: target,
    entry_quantity: null,
    pips_to_tp: null,
    pips_to_sl: null,
    rr: null,
    is_win: false,
    is_loss: false,
    net_pnl: null,
  };
}

export function strategyRdMatchesExecutionOverlays(opts: {
  matches: readonly StrategyRdMatch[];
  tradeIdFor: (match: StrategyRdMatch) => string;
  candles: readonly StrategyRdOhlcBar[];
  template: StrategyRdRuleTemplate;
  symbol?: string | null;
  assetClass?: string | null;
}): ExecutionTradeOverlay[] {
  const out: ExecutionTradeOverlay[] = [];
  for (const match of opts.matches) {
    const row = strategyRdMatchExecutionOverlay({
      tradeId: opts.tradeIdFor(match),
      match,
      candles: opts.candles,
      template: opts.template,
      symbol: opts.symbol,
      assetClass: opts.assetClass,
    });
    if (row) out.push(row);
  }
  return out;
}

/** Live Entry/SL/TP for the selected match while editing the Rules panel. */
export function previewStrategyRdLevels(opts: {
  candles: readonly StrategyRdOhlcBar[];
  barTime: string | null | undefined;
  direction: StrategyRdDirection | null | undefined;
  template: StrategyRdRuleTemplate;
  symbol?: string | null;
  assetClass?: string | null;
}): StrategyRdAppliedLevels | null {
  const barTime = opts.barTime?.trim();
  if (!barTime || !opts.candles.length) return null;
  const idx = strategyRdIndexOfBarTime(opts.candles, barTime);
  if (idx < 0) return null;
  return applyStrategyRdRulesToBar({
    candles: opts.candles,
    barIndex: idx,
    direction: opts.direction === "short" ? "short" : "long",
    template: opts.template,
    symbol: opts.symbol,
    assetClass: opts.assetClass,
  });
}
